import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { fetchGTL, exportDelegati, exportSupplenti, exportGTLMembers } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Download, FileSpreadsheet, Users, UserCheck } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

export default function ExportPage() {
  const [gtlList, setGtlList] = useState([]);
  const [selectedGTL, setSelectedGTL] = useState('');
  const [loading, setLoading] = useState(true);
  const { isSegretario } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isSegretario()) {
      navigate('/dashboard');
      return;
    }
    loadGTL();
  }, [isSegretario, navigate]);

  const loadGTL = async () => {
    try {
      const data = await fetchGTL({ limit: 100 });
      setGtlList(data.items);
    } catch (error) {
      console.error('Error loading GTL:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportDelegati = () => {
    exportDelegati();
    toast.success('Download elenco delegati avviato');
  };

  const handleExportSupplenti = () => {
    exportSupplenti();
    toast.success('Download elenco supplenti avviato');
  };

  const handleExportGTL = () => {
    if (!selectedGTL) {
      toast.error('Seleziona un GTL');
      return;
    }
    exportGTLMembers(selectedGTL);
    toast.success('Download membri GTL avviato');
  };

  if (loading) {
    return (
      <div className="p-6 lg:p-8">
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8" data-testid="export-page">
      {/* Header */}
      <div className="page-header -mx-6 lg:-mx-8 -mt-6 lg:-mt-8 mb-8 px-6 lg:px-8 py-6">
        <h1 className="font-heading text-2xl font-semibold text-slate-900">Esportazione Dati</h1>
        <p className="text-slate-500 mt-1">Scarica gli elenchi in formato CSV</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Delegati Export */}
        <Card className="card-institutional" data-testid="export-delegati-card">
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-3 bg-emerald-100 rounded-lg">
                <UserCheck className="w-6 h-6 text-emerald-700" />
              </div>
            </div>
            <CardTitle className="font-heading text-lg">Elenco Delegati</CardTitle>
            <CardDescription>
              Esporta l'elenco completo dei delegati C3i di tutti gli ordini territoriali
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              className="w-full" 
              onClick={handleExportDelegati}
              data-testid="export-delegati-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Scarica CSV Delegati
            </Button>
          </CardContent>
        </Card>

        {/* Supplenti Export */}
        <Card className="card-institutional" data-testid="export-supplenti-card">
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Users className="w-6 h-6 text-blue-700" />
              </div>
            </div>
            <CardTitle className="font-heading text-lg">Elenco Supplenti</CardTitle>
            <CardDescription>
              Esporta l'elenco dei delegati supplenti assegnati agli ordini territoriali
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              className="w-full" 
              onClick={handleExportSupplenti}
              data-testid="export-supplenti-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Scarica CSV Supplenti
            </Button>
          </CardContent>
        </Card>

        {/* GTL Export */}
        <Card className="card-institutional" data-testid="export-gtl-card">
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-3 bg-amber-100 rounded-lg">
                <FileSpreadsheet className="w-6 h-6 text-amber-700" />
              </div>
            </div>
            <CardTitle className="font-heading text-lg">Membri GTL</CardTitle>
            <CardDescription>
              Esporta l'elenco dei membri di un Gruppo di Lavoro Temporaneo
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Select value={selectedGTL} onValueChange={setSelectedGTL}>
              <SelectTrigger data-testid="select-gtl-export">
                <SelectValue placeholder="Seleziona GTL" />
              </SelectTrigger>
              <SelectContent>
                {gtlList.map(gtl => (
                  <SelectItem key={gtl.id} value={gtl.id}>
                    {gtl.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button 
              className="w-full" 
              onClick={handleExportGTL}
              disabled={!selectedGTL}
              data-testid="export-gtl-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Scarica CSV GTL
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Info */}
      <Card className="card-institutional mt-8">
        <CardContent className="p-6">
          <h3 className="font-heading font-semibold text-slate-900 mb-2">Informazioni sui file CSV</h3>
          <ul className="text-sm text-slate-600 space-y-2">
            <li>• I file sono in formato CSV (Comma-Separated Values)</li>
            <li>• Possono essere aperti con Microsoft Excel, LibreOffice Calc o Google Sheets</li>
            <li>• Contengono i dati di contatto completi delle persone</li>
            <li>• I download sono registrati nel log di sistema</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
